export interface EvidenceItem {
  type: string;
  label: string;
  detail: string;
  verified: boolean;
}

export interface TimelineItem {
  time: string;
  event: string;
}

export interface CaseItem {
  id: string;
  title: string;
  category: string;
  categoryName: string;
  summary: string;
  cover: string;
  heat: number;
  publishedAt: string;
  status: string;
  evidences: string[];
  readMinutes: number;
  tags: string[];
  conclusion: string;
  body: string[];
  timeline: TimelineItem[];
  evidenceList: EvidenceItem[];
}

export const categories = [
  { id: "all", name: "全部档案", intro: "站内全部整理完毕的爆料档案" },
  { id: "timeline", name: "行程时间线", intro: "以时间轴对照公开口径与第三方路透" },
  { id: "business", name: "商业内幕", intro: "选品、分佣、机构结算相关的内部线索" },
  { id: "contract", name: "合约风波", intro: "解约、档期、条款争议的来龙去脉" },
  { id: "reputation", name: "圈内口碑", intro: "剧组与项目现场的口碑类爆料" },
];

export const cases: CaseItem[] = [
  {
    id: "case-01",
    title: "某选秀男艺人深夜行程口径不一，三处时间差待核对",
    category: "timeline",
    categoryName: "行程时间线",
    summary:
      "粉丝后援会整理的公开行程单，与第三方路透、直播自述出现三处时间对不上。我们把三份材料逐项并排，标出矛盾点与可能的说明空间。",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20flat%20lay%20of%20blurred%20papers%20timeline%20notes%20and%20a%20brass%20magnifying%20glass%20on%20warm%20aged%20paper%2C%20crimson%20and%20gold%20tones%2C%20soft%20lamp%20light%2C%20cinematic%20no%20faces&width=800&height=600&seq=gt-cover-01&orientation=landscape",
    heat: 98600,
    publishedAt: "2026-09-08",
    status: "证据链补充中",
    evidences: ["截图", "行程单", "路透照片"],
    readMinutes: 7,
    tags: ["深夜行程", "时间线矛盾", "口径对照"],
    conclusion:
      "目前三份材料的时间差集中在 23:40 至 01:20 之间，尚无一方给出完整说明，标记为「待补充」。",
    body: [
      "整件事的起点并不复杂：后援会发布了一份当日行程单，标注了收工时间。几小时后，有路人晒出另一个时间点的同框照片，时间戳与行程单不一致。",
      "我们又找到该艺人当晚直播里的一段口述，把三段材料对齐后，发现至少有三处无法同时成立的时间点。需要说明的是，行程单本身属于活动方口径，存在提前收工或临时改动的空间。",
      "本文不替任何一方下结论，只把材料摆在一起。若后续有新的截图或说明，本条档案会继续补录。",
    ],
    timeline: [
      { time: "09-06 21:30", event: "后援会发布当日行程单，标注 22:50 收工" },
      { time: "09-06 23:40", event: "第三方路透照片出现，定位显示仍在市区" },
      { time: "09-07 00:15", event: "艺人直播提及「早已到家休息」" },
      { time: "09-07 01:20", event: "有账号补充第二组路透，时间戳再次冲突" },
      { time: "09-08 10:00", event: "本条档案建立，标注三处待核对时间点" },
    ],
    evidenceList: [
      {
        type: "截图",
        label: "后援会行程单原图",
        detail: "含发布时间与收工标注，未做裁剪，保留原始尺寸。",
        verified: true,
      },
      {
        type: "路透照片",
        label: "第三方街拍第一组",
        detail: "照片保留 EXIF 时间，定位与行程单存在明显偏差。",
        verified: true,
      },
      {
        type: "录音",
        label: "直播口述片段",
        detail: "长度约 40 秒，已标注对应时间节点，未做变速处理。",
        verified: false,
      },
    ],
  },
  {
    id: "case-02",
    title: "某带货主播选品分成被前员工爆料，坑位费与返点被指双轨结算",
    category: "business",
    categoryName: "商业内幕",
    summary:
      "一名自称前选品助理的爆料人给出内部结算表截图，称同一场直播存在两套分成口径。涉及金额与品牌方口径存在出入，我们把能核对的数字都列了出来。",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20arrangement%20of%20blurred%20accounting%20sheets%2C%20calculator%20and%20sealing%20wax%20stamp%20on%20warm%20desk%2C%20crimson%20and%20amber%20tones%2C%20soft%20side%20light%2C%20cinematic%20no%20faces&width=800&height=600&seq=gt-cover-02&orientation=landscape",
    heat: 87300,
    publishedAt: "2026-09-07",
    status: "证据链补充中",
    evidences: ["截图", "表格", "聊天记录"],
    readMinutes: 6,
    tags: ["选品佣金", "分佣双轨", "内部结算"],
    conclusion:
      "爆料表格与品牌方公开报价单在两项数字上可相互印证，其余三项缺少对照材料，暂列「待补」。",
    body: [
      "爆料人给出的核心材料是一张内部结算表，表里同一场直播出现了两列分成比例。按爆料人的说法，一列是对品牌方公示的，另一列是实际结算用的。",
      "我们比对了品牌方此前对外公布的合作报价，发现其中两项数字与表格吻合，这部分可信度较高；另外三项缺少第三方材料，暂时无法判断。",
      "选品分佣在行业里本就存在多种结算方式，本文只呈现材料本身可核对的部分，不对爆料人的动机做推测。",
    ],
    timeline: [
      { time: "09-04 18:00", event: "爆料账号发布第一张结算表截图" },
      { time: "09-05 11:20", event: "爆料人补充一段选品群聊天记录" },
      { time: "09-06 09:00", event: "品牌方客服回应称「以合同为准」" },
      { time: "09-07 15:40", event: "本条档案建立，标注两项可印证数字" },
    ],
    evidenceList: [
      {
        type: "表格",
        label: "内部结算表截图",
        detail: "含两列分成比例，行内品牌名已做模糊处理。",
        verified: true,
      },
      {
        type: "聊天记录",
        label: "选品群对话片段",
        detail: "提及坑位费外的返点口径，发言人身份未核实。",
        verified: false,
      },
      {
        type: "截图",
        label: "品牌方公开报价单",
        detail: "取自品牌官方账号历史发文，两项数字可与表格对照。",
        verified: true,
      },
    ],
  },
  {
    id: "case-03",
    title: "某新生代歌手解约风波：合约期限与分成条款各执一词",
    category: "contract",
    categoryName: "合约风波",
    summary:
      "双方先后发布声明，一方称合约到期，另一方出示了一份带签署日期的续约页。我们把两份声明的关键句逐条对照，提示条款存在不同的解释空间。",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20scene%20of%20a%20folded%20sealed%20contract%20with%20red%20ribbon%20and%20a%20wax%20seal%20on%20warm%20linen%20surface%2C%20crimson%20and%20gold%20palette%2C%20soft%20window%20light%2C%20cinematic%20no%20faces&width=800&height=600&seq=gt-cover-03&orientation=landscape",
    heat: 76400,
    publishedAt: "2026-09-05",
    status: "已归档",
    evidences: ["声明截图", "合同局部", "时间线"],
    readMinutes: 5,
    tags: ["解约", "合约期限", "条款解释"],
    conclusion:
      "争议焦点在于续约页是否生效，两份材料对签署时间与生效条件的表述不完全一致，需第三方介入才能定论。",
    body: [
      "事件的公开部分只有两份声明。歌手方称合约自然到期，公司方则出示一张带签署日期的续约页，主张合约仍在存续期内。",
      "我们把两份声明里涉及期限与分成的句子抽出来并排放在一起，能看到双方对「生效条件」的理解明显不同，这可能才是分歧的根源。",
      "合约类纠纷往往涉及大量不对外披露的条款，本文仅根据双方公开发布的内容整理，不代表任何一方的最终结论。",
    ],
    timeline: [
      { time: "08-28 12:00", event: "歌手工作室发布单方面解约声明" },
      { time: "08-29 16:30", event: "公司方回应，出示续约页局部" },
      { time: "08-31 10:00", event: "双方各补充一次说明，焦点转向生效条件" },
      { time: "09-05 14:00", event: "本条档案标记为「已归档」，等待后续进展" },
    ],
    evidenceList: [
      {
        type: "声明截图",
        label: "双方公开声明对照",
        detail: "两段关于期限的表述已逐句并排，标注用词差异。",
        verified: true,
      },
      {
        type: "合同局部",
        label: "续约页局部照片",
        detail: "仅露出签署日期与页脚，条款正文未公开。",
        verified: false,
      },
      {
        type: "时间线",
        label: "公开事件时间轴",
        detail: "依据两条官方发文时间戳整理，无第三方材料。",
        verified: true,
      },
    ],
  },
  {
    id: "case-04",
    title: "某剧组「背锅」传闻：替身爆料通告排班与成片镜头的落差",
    category: "reputation",
    categoryName: "圈内口碑",
    summary:
      "一名动作替身爆料称自己承担了大部分高难度镜头，成片署名与镜头分配却与通告排班对不上。我们整理了可核对的通告节点。",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20scene%20of%20a%20film%20clapperboard%20and%20call%20sheet%20papers%20on%20warm%20studio%20floor%20with%20soft%20spotlight%2C%20crimson%20and%20amber%20tones%2C%20cinematic%20no%20faces&width=800&height=600&seq=gt-cover-04&orientation=landscape",
    heat: 61200,
    publishedAt: "2026-09-03",
    status: "证据链补充中",
    evidences: ["通告单", "聊天记录", "时间线"],
    readMinutes: 5,
    tags: ["替身", "通告排班", "署名争议"],
    conclusion:
      "通告单显示该替身确实参与了多个高难度日，成片署名情况需以正式片尾为准，档案保留「待核对」标记。",
    body: [
      "爆料人提供的是一份通告单局部，上面数天的拍摄内容都标注为高难度动作。爆料人称这些日子的成片镜头被集中算在了主演名下。",
      "需要区分的是：替身参与拍摄与署名分配是两件事，行业里署名通常取决于合同约定。我们把通告单能看清的节点整理成时间轴，供读者自行判断。",
      "剧组方面暂未公开回应，本条档案等待正式的片尾署名与合同信息。",
    ],
    timeline: [
      { time: "08-30 20:00", event: "替身在个人账号发布通告单局部" },
      { time: "09-01 13:10", event: "同组工作人员私信补充一段排班对话" },
      { time: "09-03 09:30", event: "本条档案建立，标出四个可核对拍摄日" },
    ],
    evidenceList: [
      {
        type: "通告单",
        label: "拍摄通告局部",
        detail: "四个拍摄日标注为高强度动作戏，含场次编号。",
        verified: true,
      },
      {
        type: "聊天记录",
        label: "工作人员排班对话",
        detail: "提到「替身跟完整组」，发言人身份未核实。",
        verified: false,
      },
      {
        type: "时间线",
        label: "拍摄节点时间轴",
        detail: "基于通告单日期整理，无成片对照材料。",
        verified: true,
      },
    ],
  },
  {
    id: "case-05",
    title: "某女演员生日行程时间线被扒：机场路透与官宣定位对不上",
    category: "timeline",
    categoryName: "行程时间线",
    summary:
      "生日当天发布的定位与同日机场路透存在明显地理偏差。我们把公开可查的三组时间点排成一条线，指出需要说明的位置。",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20scene%20of%20an%20airport%20departure%20board%20and%20a%20paper%20timeline%20on%20warm%20surface%2C%20crimson%20and%20gold%20tones%2C%20soft%20ambient%20light%2C%20cinematic%20no%20faces&width=800&height=600&seq=gt-cover-05&orientation=landscape",
    heat: 55800,
    publishedAt: "2026-09-01",
    status: "证据链补充中",
    evidences: ["路透照片", "定位截图", "时间线"],
    readMinutes: 4,
    tags: ["生日行程", "机场路透", "定位偏差"],
    conclusion:
      "定位偏差约在两个时区之间，可能受发布延迟影响，暂无直接证据表明矛盾成立。",
    body: [
      "争议点很小：生日当天一条动态显示在海边，而同日机场路透显示人在另一座城市。两者之间相隔数小时，理论上存在赶得上的可能。",
      "我们把三组公开时间点对齐后发现，中间确实留有一段可移动的时间窗口，但也存在动态延迟发布的空间。",
      "这条档案的价值不在结论，而在于把「能不能对得上」这个问题讲清楚。",
    ],
    timeline: [
      { time: "08-25 08:20", event: "机场路透出现，时间戳为上午" },
      { time: "08-25 14:00", event: "生日动态发布，定位显示海边" },
      { time: "08-25 20:40", event: "第三组路透出现，定位回到市区" },
      { time: "09-01 11:00", event: "本条档案建立，标注两个待核对窗口" },
    ],
    evidenceList: [
      {
        type: "路透照片",
        label: "机场路透第一组",
        detail: "保留拍摄时间，与动态发布时间相隔约五小时。",
        verified: true,
      },
      {
        type: "定位截图",
        label: "生日动态定位",
        detail: "定位为海边，未显示精确坐标。",
        verified: true,
      },
      {
        type: "时间线",
        label: "三组时间点轴",
        detail: "依据公开时间戳整理，存在延迟发布的可能。",
        verified: false,
      },
    ],
  },
  {
    id: "case-06",
    title: "某机构内部考核表流出，达人流量与结算等级被指直接挂钩",
    category: "business",
    categoryName: "商业内幕",
    summary:
      "一份据称是内部考核表的文件显示，达人结算等级与单条内容播放量强绑定。我们把表内规则与达人公开吐槽逐条对应。",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20scene%20of%20a%20spreadsheet%20printout%20and%20a%20ranking%20chart%20on%20warm%20desk%2C%20crimson%20and%20amber%20tones%2C%20soft%20lamp%20light%2C%20cinematic%20no%20faces&width=800&height=600&seq=gt-cover-06&orientation=landscape",
    heat: 49700,
    publishedAt: "2026-08-30",
    status: "已归档",
    evidences: ["表格", "直播片段", "聊天记录"],
    readMinutes: 5,
    tags: ["考核表", "结算等级", "播放量绑定"],
    conclusion:
      "表内规则与多位达人的公开吐槽高度对应，但仍缺少机构方确认，档案标注「待回应」。",
    body: [
      "流出的考核表把达人分成几个等级，等级直接决定结算比例，而升级门槛写的是「近三十天单条平均播放量」。",
      "巧合的是，过去一段时间里有多位达人公开抱怨「一条内容没起来，整个月白干」，措辞与表格逻辑高度吻合。",
      "表格真伪仍需机构确认，我们能做的是把两条信息放在一起，让读者看到对应关系。",
    ],
    timeline: [
      { time: "08-26 22:10", event: "考核表截图首次出现" },
      { time: "08-27 19:30", event: "两位达人直播中提及结算规则" },
      { time: "08-30 10:00", event: "本条档案标记为「已归档」" },
    ],
    evidenceList: [
      {
        type: "表格",
        label: "内部考核表截图",
        detail: "含四个等级与对应结算比例，机构名已模糊。",
        verified: false,
      },
      {
        type: "直播片段",
        label: "达人直播吐槽",
        detail: "两段片段均提及播放量与结算挂钩，已保留原声。",
        verified: true,
      },
      {
        type: "聊天记录",
        label: "运营群通知片段",
        detail: "提到「按等级走」，缺少上下文。",
        verified: false,
      },
    ],
  },
  {
    id: "case-07",
    title: "某综艺常驻嘉宾中途退出，档期冲突与违约金传闻交织",
    category: "contract",
    categoryName: "合约风波",
    summary:
      "录制进行到中段，一位常驻嘉宾确认不再参与后续。节目组称档期冲突，知情账号称涉及违约条款，我们把公开信息排成时间线。",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20scene%20of%20a%20studio%20scheduling%20board%20and%20marked%20calendar%20on%20warm%20surface%2C%20crimson%20and%20gold%20tones%2C%20soft%20studio%20light%2C%20cinematic%20no%20faces&width=800&height=600&seq=gt-cover-07&orientation=landscape",
    heat: 44100,
    publishedAt: "2026-08-27",
    status: "已归档",
    evidences: ["声明截图", "日程表", "时间线"],
    readMinutes: 4,
    tags: ["中途退出", "档期冲突", "违约条款"],
    conclusion:
      "节目组与嘉宾方对退出原因表述一致为「档期」，违约金部分仅有非官方账号提及，未获证实。",
    body: [
      "节目组发布的说明很简短，只提「因档期安排」。随后有账号声称涉及违约金，但没有任何一方出示材料。",
      "我们把录制日程与嘉宾此后的公开行程对齐，确实存在时间上的重叠，这至少说明「档期冲突」的说法不是空穴来风。",
      "违约金部分缺少证据，本条档案不采纳为事实，仅作记录。",
    ],
    timeline: [
      { time: "08-22 15:00", event: "节目组确认嘉宾不再参与后续录制" },
      { time: "08-23 11:30", event: "非官方账号提出违约金说法" },
      { time: "08-27 09:00", event: "本条档案标记为「已归档」" },
    ],
    evidenceList: [
      {
        type: "声明截图",
        label: "节目组官方说明",
        detail: "全文仅两句，未提及违约相关字样。",
        verified: true,
      },
      {
        type: "日程表",
        label: "后续公开行程对照",
        detail: "与录制日期存在重叠，支持档期冲突说法。",
        verified: true,
      },
      {
        type: "时间线",
        label: "事件公开时间轴",
        detail: "依据三条公开发文时间戳整理。",
        verified: true,
      },
    ],
  },
  {
    id: "case-08",
    title: "某音乐节压轴换人风波：主办方与艺人团队各说各话",
    category: "reputation",
    categoryName: "圈内口碑",
    summary:
      "开演前一晚临时更换压轴阵容，主办方称设备与时长受限，艺人团队称彩排安排被临时更改。我们把两份说明的关键句并排放置。",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20scene%20of%20an%20empty%20concert%20stage%20with%20warm%20stage%20lights%20and%20a%20setlist%20paper%20on%20floor%2C%20crimson%20and%20amber%20tones%2C%20cinematic%20no%20faces&width=800&height=600&seq=gt-cover-08&orientation=landscape",
    heat: 38900,
    publishedAt: "2026-08-24",
    status: "证据链补充中",
    evidences: ["声明截图", "彩排安排", "时间线"],
    readMinutes: 4,
    tags: ["压轴换人", "彩排安排", "临时调整"],
    conclusion:
      "双方对彩排时间被调整这一点表述一致，但调整原因各执一词，需现场工作群记录才能进一步核对。",
    body: [
      "换人的消息在演出前一晚放出，观众反应强烈。主办方随后说明是设备调试与整体时长的问题。",
      "艺人团队的说法是彩排安排被临时更改，导致无法按原方案呈现。两份说明在「彩排被调整」这一点上是一致的。",
      "分歧在于调整的原因与责任归属，本文不站队，只把两份说明的关键句放在一起。",
    ],
    timeline: [
      { time: "08-20 21:00", event: "主办方发布阵容调整通知" },
      { time: "08-21 10:00", event: "艺人团队发布说明" },
      { time: "08-24 09:00", event: "本条档案建立，标注一处共同表述" },
    ],
    evidenceList: [
      {
        type: "声明截图",
        label: "两份官方说明对照",
        detail: "涉及彩排时间的句子已并排，用词差异已标注。",
        verified: true,
      },
      {
        type: "彩排安排",
        label: "彩排时间表局部",
        detail: "显示时间有过一次改动，改动原因未注明。",
        verified: false,
      },
      {
        type: "时间线",
        label: "风波时间轴",
        detail: "依据两条公开说明的时间戳整理。",
        verified: true,
      },
    ],
  },
];